import type { OnInit } from '@angular/core';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import type { OnyxCallbackOption, OnyxTableData } from '@onyx/angular';
import {
  ActionHelper,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxSectionComponent,
  OnyxTableComponent,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { differenceBy } from 'lodash';
import { SHORT_PAGINATION } from '../../../../../common/constants/common/short-pagination';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { ContractorsRoute } from '../../../contractors/common/enums/contractors-route';
import { ContractorHelper } from '../../../contractors/common/helpers/contractor.helper';
import type {
  Contractor,
  SimplifiedContractor,
} from '../../../contractors/common/interfaces/contractor';
import { ASSIGNED_CONTRACTORS_COLUMNS } from '../../common/constants/assigned-contractors-columns';
import { ASSIGNED_CONTRACTORS_NOT_FOUND } from '../../common/constants/assigned-contractors-not-found';

type PointOfInterestAssignedContractorsFormArray = ReturnType<
  (typeof PointOfInterestAssignedContractorsFormComponent)['buildForm']
>;
export type PointOfInterestAssignedContractorsFormGroup = ReturnType<
  (typeof PointOfInterestAssignedContractorsFormComponent)['buildAssignedContractorForm']
>;

@Component({
  selector: 'app-point-of-interest-assigned-contractors-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxTableComponent,
  ],
  templateUrl: './point-of-interest-assigned-contractors-form.component.html',
  styleUrl: './point-of-interest-assigned-contractors-form.component.scss',
})
export class PointOfInterestAssignedContractorsFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly contractorHelper = inject(ContractorHelper);

  public readonly form =
    input.required<PointOfInterestAssignedContractorsFormArray>();
  public readonly mode = input(OnyxFormMode.ADD);

  protected readonly I18N = 'pointsOfInterest.contractors';
  protected readonly COLUMNS = ASSIGNED_CONTRACTORS_COLUMNS;
  protected readonly NOT_FOUND = ASSIGNED_CONTRACTORS_NOT_FOUND;
  protected readonly CONTRACTORS_SOURCE =
    this.contractorHelper.getContractorsSource();

  protected readonly DictionaryCode = DictionaryCode;

  protected pagination = signal(SHORT_PAGINATION);
  protected contractors = signal<Contractor[] | null>(null);

  protected get contractorsControl() {
    return this.form().controls.contractors;
  }

  public ngOnInit(): void {
    const selectedContractorsControl = this.form().controls.selectedContractors;
    selectedContractorsControl.setValue(
      this.contractorsControl.getRawValue().map((group) => group.uuid),
    );

    effect(
      () => {
        const selectedContractors = this.contractors();
        if (
          !selectedContractors?.length &&
          !(
            selectedContractorsControl.touched &&
            selectedContractorsControl.dirty
          )
        ) {
          return;
        }

        const activeContractors = this.contractorsControl
          .getRawValue()
          .map((group) => group.contractor);

        const removedContractors = differenceBy(
          activeContractors,
          selectedContractors ?? [],
          'uuid',
        );
        for (const contractor of removedContractors) {
          const index = this.contractorsControl
            .getRawValue()
            .findIndex((group) => group.uuid === contractor.uuid);
          this.contractorsControl.removeAt(index);
        }

        const addedContractors = differenceBy(
          selectedContractors,
          activeContractors,
          'uuid',
        );
        for (const contractor of addedContractors) {
          this.contractorsControl.push(
            PointOfInterestAssignedContractorsFormComponent.buildAssignedContractorForm(
              this.fb,
              contractor,
            ),
          );
        }

        this.contractorsControl.controls = [
          ...this.contractorsControl.controls,
        ];
      },
      { injector: this.injector },
    );
  }

  protected addContractor(): void {
    ActionHelper.openLink(ContractorsRoute.ADD_CONTRACTOR);
  }

  protected getContractorOptions = (
    data: OnyxTableData<PointOfInterestAssignedContractorsFormGroup>,
  ): OnyxCallbackOption[] => {
    return [
      {
        name: 'labels.show',
        value: () =>
          this.contractorHelper.openModal(data.item.getRawValue().contractor),
      },
    ];
  };

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      selectedContractors: fb.control<string[]>([]),
      contractors: fb.array<PointOfInterestAssignedContractorsFormGroup>([]),
    });
  }

  public static buildAssignedContractorForm(
    fb: NonNullableFormBuilder,
    contractor: SimplifiedContractor,
  ) {
    return fb.group({
      contractor: fb.control({ value: contractor, disabled: true }),
      uuid: fb.control({ value: contractor.uuid, disabled: true }),
      gate: fb.control<string | null>(null, [onyxTextFieldLengthValidator]),
    });
  }
}
